import React, { useState, useEffect, useRef } from 'react';
import DatePicker from './DatePicker';
import { api } from '../api';
import { refreshCache } from '../store';
import { formatMoney, formatDate, todayISO, toISODate } from '../dateUtils';
import { categoryEmoji } from '../categoryEmoji';
import { plannedTransfersNet, plannedDebtReturnsNet, normPlanned, totalPlanned, startingBalance, isIncludedAccount, effectivePlanned, dailyBudgetModel } from '../balance';
import Modal from './Modal';
import TransactionForm from './TransactionForm';

export default function PeriodForm({ period, categories, accounts, transactions, onCancel, readOnly }) {
  const [startDate, setStartDate] = useState(period?.startDate || '');
  const [endDate, setEndDate] = useState(period?.endDate || '');
  const [plannedIncomes, setPlannedIncomes] = useState(() => normPlanned(period?.plannedIncomes));
  const [plannedExpenses, setPlannedExpenses] = useState(() => normPlanned(period?.plannedExpenses));
  const [plannedTransfers, setPlannedTransfers] = useState(period?.plannedTransfers || []);
  const [plannedDebtReturns, setPlannedDebtReturns] = useState(period?.plannedDebtReturns || []);
  const [localCategories, setLocalCategories] = useState(categories || []);
  // Локальная копия счетов, чтобы можно было прямо здесь добавить нового человека
  const [localAccounts, setLocalAccounts] = useState(accounts || []);
  // Строка создания новой категории: { type: 'income'|'expense', name }
  const [newCat, setNewCat] = useState(null);
  // Строка создания нового человека в разделе возвратов долгов: { index, name }
  const [newDebtPerson, setNewDebtPerson] = useState(null);
  // Формы добавления операции (сумма, дата, комментарий) по категории
  const [incomeOp, setIncomeOp] = useState({ category: '', amount: '', date: '', comment: '', recurring: false });
  const [expenseOp, setExpenseOp] = useState({ category: '', amount: '', date: '', comment: '', recurring: false });
  // Операция, которую редактируют: { kind, gIndex, oIndex, amount, date, comment }
  const [editingOp, setEditingOp] = useState(null);
  // Открытая секция аккордеона: 'income' | 'expense' | 'transfer' | 'debt' | null
  const [openSection, setOpenSection] = useState(null);
  // Секции, у которых закончилась анимация разворачивания — тогда overflow видимый,
  // чтобы выпадающий календарь дат не обрезался блоком.
  const [expandedDone, setExpandedDone] = useState({});
  // Модальное окно добавления: 'income' | 'expense' | 'transfer' | 'debt' | null
  const [addModal, setAddModal] = useState(null);
  // Черновики добавления перевода / возврата долга
  const [transferDraft, setTransferDraft] = useState({ fromAccountId: '', toAccountId: '', amount: '', date: '' });
  const [debtDraft, setDebtDraft] = useState({ debtAccountId: '', accountId: '', amount: '', date: '' });
  // Индекс редактируемого перевода/долга (null = добавление нового)
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [editingDebt, setEditingDebt] = useState(null);
  // Операция, которую подтверждают «выполнить»: открывает окно с предзаполненными данными
  const [confirmOp, setConfirmOp] = useState(null);
  // Автосохранение: id сохранённого периода и защита от лишних сохранений
  const periodIdRef = useRef(period?.id || null);
  const isFirstRef = useRef(true);
  const skipNextSaveRef = useRef(false);
  const toggleSection = (type) => {
    const next = openSection === type ? null : type;
    // при сворачивании сразу возвращаем overflow:hidden для чистой анимации
    setExpandedDone(prev => {
      const out = { ...prev };
      if (openSection) out[openSection] = false;
      return out;
    });
    setOpenSection(next);
    if (next) {
      setTimeout(() => setExpandedDone(prev => ({ ...prev, [next]: true })), 350);
    }
  };

  const allAccounts = localAccounts;
  const regularAccounts = allAccounts.filter(a => !a.isDebt);
  const debtAccounts = allAccounts.filter(a => a.isDebt);
  const allCategories = localCategories;
  const incomeCategories = allCategories.filter(c => c.type === 'income');
  const expenseCategories = allCategories.filter(c => c.type === 'expense');

  // Имя категории по id (для подсчёта фактических доходов/расходов по категории за период)
  const categoryNameById = (id) => {
    const c = (allCategories || []).find(x => String(x.id) === String(id));
    return c ? c.name : '';
  };

  // Имя обычного счёта по id
  const accountNameById = (id) => {
    if (id === null || id === undefined || id === '') return '';
    const a = (allAccounts || []).find(x => String(x.id) === String(id));
    return a ? (a.name || '') : '';
  };

  // Имя человека (долговой счёт) по id
  const debtAccountNameById = (id) => {
    if (id === null || id === undefined || id === '') return '';
    const a = (allAccounts || []).find(x => String(x.id) === String(id));
    return a ? (a.person || a.name || '') : '';
  };

  const updateTransfer = (index, field, value) => {
    const newArr = [...plannedTransfers];
    newArr[index][field] = value;
    setPlannedTransfers(newArr);
  };

  const updateDebtReturn = (index, field, value) => {
    const newArr = [...plannedDebtReturns];
    newArr[index][field] = value;
    setPlannedDebtReturns(newArr);
  };

  const removeTransfer = (index) => setPlannedTransfers(plannedTransfers.filter((_, i) => i !== index));
  const removeDebtReturn = (index) => setPlannedDebtReturns(plannedDebtReturns.filter((_, i) => i !== index));

  // Начать редактирование перевода/долга (открывает ту же модалку, что и добавление)
  const startEditTransfer = (index) => {
    const t = plannedTransfers[index];
    setTransferDraft({ fromAccountId: String(t.fromAccountId), toAccountId: String(t.toAccountId), amount: String(t.amount), date: t.date || '' });
    setEditingTransfer(index);
    setAddModal('transfer');
  };
  const startEditDebtReturn = (index) => {
    const d = plannedDebtReturns[index];
    setDebtDraft({ debtAccountId: String(d.debtAccountId), accountId: String(d.accountId), amount: String(d.amount), date: d.date || '' });
    setEditingDebt(index);
    setAddModal('debt');
  };

  // Создать новую категорию прямо из формы добавления операции
  const handleCreateCategory = async (type) => {
    const name = (newCat && newCat.name || '').trim();
    if (!name) return;
    try {
      const created = await api.createCategory({ name, type });
      setLocalCategories(prev => [...prev, created]);
      if (type === 'income') setIncomeOp(prev => ({ ...prev, category: created.name }));
      else setExpenseOp(prev => ({ ...prev, category: created.name }));
      refreshCache();
    } catch (e) {
      console.error(e);
    }
    setNewCat(null);
  };

  // Создать нового человека (долговой счёт) прямо из строки возврата долга
  const handleCreateDebtPerson = async (index) => {
    const name = (newDebtPerson && newDebtPerson.name || '').trim();
    if (!name) return;
    try {
      const created = await api.createAccount({ name, isDebt: true, person: name, includeInBalance: false });
      setLocalAccounts(prev => [...prev, created]);
      updateDebtReturn(index, 'debtAccountId', created.id);
      refreshCache();
    } catch (e) {
      console.error(e);
    }
    setNewDebtPerson(null);
  };

  const onIncomeCatChange = (value) => {
    if (value === '__new__') setNewCat({ type: 'income', name: '' });
    else setIncomeOp(prev => ({ ...prev, category: value }));
  };
  const onExpenseCatChange = (value) => {
    if (value === '__new__') setNewCat({ type: 'expense', name: '' });
    else setExpenseOp(prev => ({ ...prev, category: value }));
  };

  // ---------- Операции по категориям ----------
  // Уникальный id операции (для отметки «выполнено» и сохранения)
  const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

  // Новый период: переносим операции с галочкой «повторять каждый месяц» из последнего периода
  useEffect(() => {
    if (period) return; // редактируем существующий — не трогаем
    let cancelled = false;
    api.getPeriods().then(list => {
      if (cancelled || !list || list.length === 0) return;
      const last = list.reduce((a, b) => (a.startDate >= b.startDate ? a : b));
      const carry = (groups) => normPlanned(groups)
        .filter(g => (g.operations || []).some(o => o.recurring))
        .map(g => ({
          ...g,
          finished: false,
          operations: (g.operations || [])
            .filter(o => o.recurring)
            .map(o => ({ id: genId(), recurringId: o.recurringId || genId(), amount: o.amount, date: o.date || '', comment: o.comment || '', done: false, recurring: true })),
        }));
      const incomes = carry(last.plannedIncomes);
      const expenses = carry(last.plannedExpenses);
      if (incomes.length || expenses.length) skipNextSaveRef.current = true;
      if (incomes.length) setPlannedIncomes(incomes);
      if (expenses.length) setPlannedExpenses(expenses);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [period]);

  const addOperation = (kind) => {
    const isIncome = kind === 'income';
    const op = isIncome ? incomeOp : expenseOp;
    const setOp = isIncome ? setIncomeOp : setExpenseOp;
    const setList = isIncome ? setPlannedIncomes : setPlannedExpenses;
    const category = (op.category || '').trim();
    if (!category) { alert('Выберите категорию'); return false; }
    const amt = Number(op.amount);
    if (!amt || amt <= 0) { alert('Укажите сумму'); return false; }
    setList(prev => {
      const idx = prev.findIndex(g => g.category === category);
      if (idx === -1) {
        // Категории ещё нет — создаём её с этой операцией (бюджет = сумма платежей)
        return [...prev, { category, amount: 0, operations: [{ id: genId(), amount: amt, date: op.date || '', comment: (op.comment || '').trim(), done: false, recurring: !!op.recurring, recurringId: op.recurring ? genId() : null }] }];
      }
      // Категория уже есть — добавляем операцию, она расходует бюджет
      return prev.map((g, i) => i === idx
        ? { ...g, operations: [...g.operations, { id: genId(), amount: amt, date: op.date || '', comment: (op.comment || '').trim(), done: false, recurring: !!op.recurring, recurringId: op.recurring ? genId() : null }] }
        : g);
    });
    setOp({ category: '', amount: '', date: '', comment: '', recurring: false });
    return true;
  };

  const removeGroup = (kind, index) => {
    const setList = kind === 'income' ? setPlannedIncomes : setPlannedExpenses;
    setList(prev => prev.filter((_, i) => i !== index));
  };
  const removeOperation = (kind, gIndex, oIndex) => {
    const setList = kind === 'income' ? setPlannedIncomes : setPlannedExpenses;
    setList(prev => prev.map((g, i) => i === gIndex
      ? { ...g, operations: g.operations.filter((_, j) => j !== oIndex) }
      : g));
  };

  // Начать редактирование операции
  const startEditOp = (kind, gIndex, oIndex, o) => {
    setEditingOp({ kind, gIndex, oIndex, amount: String(o.amount), date: o.date || '', comment: o.comment || '', recurring: !!o.recurring });
  };

  // Сохранить отредактированную операцию
  const saveEditOp = () => {
    if (!editingOp) return;
    const { kind, gIndex, oIndex, amount, date, comment, recurring } = editingOp;
    const amt = Number(amount);
    if (!amt || amt <= 0) { alert('Укажите сумму'); return; }
    const setList = kind === 'income' ? setPlannedIncomes : setPlannedExpenses;
    setList(prev => prev.map((g, i) => i === gIndex
      ? { ...g, operations: g.operations.map((o, j) => j === oIndex
          ? { ...o, amount: amt, date: date || '', comment: (comment || '').trim(), recurring: !!recurring, recurringId: recurring ? (o.recurringId || genId()) : null }
          : o) }
      : g));
    setEditingOp(null);
  };

  // Закончить / вернуть расходы (доходы) по категории — с подтверждением,
  // т.к. после этого учитываются только выполненные операции и суточный расчёт пересчитывается.
  const finishCategory = (kind, gIndex) => {
    const isIncome = kind === 'income';
    const groups = isIncome ? plannedIncomes : plannedExpenses;
    const g = groups[gIndex];
    const setList = isIncome ? setPlannedIncomes : setPlannedExpenses;
    if (g.finished) {
      setList(prev => prev.map((gr, i) => i === gIndex ? { ...gr, finished: false } : gr));
      return;
    }
    const word = isIncome ? 'доходы' : 'расходы';
    if (window.confirm(
      `Закончить ${word} по категории «${g.category}» в этом периоде?\n\n` +
      'После этого будет учитываться фактическая сумма по этой категории, а неистраченный остаток вернётся в бюджет.'
    )) {
      setList(prev => prev.map((gr, i) => i === gIndex ? { ...gr, finished: true } : gr));
    }
  };

  // ===== «Выполнить» плановую операцию =====
  // Открыть окно подтверждения с данными, предзаполненными из плана.
  const openConfirmOp = (kind, gIndex, oIndex, o, category) => {
    setConfirmOp({ kind, gIndex, oIndex, category: category || '', amount: o.amount, date: o.date || '', comment: o.comment || '' });
  };
  const openConfirmTransfer = (index) => {
    const t = plannedTransfers[index];
    setConfirmOp({ kind: 'transfer', index, fromAccountId: t.fromAccountId, toAccountId: t.toAccountId, amount: t.amount, date: t.date || '' });
  };
  const openConfirmDebtReturn = (index) => {
    const d = plannedDebtReturns[index];
    setConfirmOp({ kind: 'debtReturn', index, accountId: d.accountId, debtAccountId: d.debtAccountId, amount: d.amount, date: d.date || '' });
  };

  const getCategoryIdByName = (name) => {
    const c = allCategories.find(x => x.name === name);
    return c ? c.id : null;
  };

  // Данные для формы подтверждения в зависимости от типа плана
  const confirmTx = confirmOp ? {
    ...(confirmOp.kind === 'transfer' ? {
      type: 'transfer',
      accountId: confirmOp.fromAccountId,
      toAccountId: confirmOp.toAccountId,
      amount: confirmOp.amount,
      date: confirmOp.date || startDate || todayISO(),
      description: 'Перевод между счетами',
    } : confirmOp.kind === 'debtReturn' ? {
      type: 'debt',
      debtKind: 'return',
      accountId: confirmOp.accountId,
      debtAccountId: confirmOp.debtAccountId,
      amount: confirmOp.amount,
      date: confirmOp.date || startDate || todayISO(),
      description: 'Возврат долга',
    } : {
      type: confirmOp.kind,
      categoryId: getCategoryIdByName(confirmOp.category),
      amount: confirmOp.amount,
      date: confirmOp.date || startDate || todayISO(),
      description: confirmOp.comment || confirmOp.category || '',
    }),
  } : null;

  // Подтвердить «выполнить» — создать реальную транзакцию и отметить плановую как сделанную
  const handleConfirmOp = async (data) => {
    try {
      await api.createTransaction(data);
      const op = confirmOp;
      if (!op) return;
      if (op.kind === 'income' || op.kind === 'expense') {
        const setList = op.kind === 'income' ? setPlannedIncomes : setPlannedExpenses;
        setList(prev => prev.map((g, i) => i === op.gIndex
          ? { ...g, operations: g.operations.map((o, j) => j === op.oIndex ? { ...o, done: true } : o) }
          : g));
      } else if (op.kind === 'transfer') {
        setPlannedTransfers(prev => prev.map((x, i) => i === op.index ? { ...x, done: true } : x));
      } else if (op.kind === 'debtReturn') {
        setPlannedDebtReturns(prev => prev.map((x, i) => i === op.index ? { ...x, done: true } : x));
      }
      refreshCache();
      window.dispatchEvent(new Event('data:changed'));
    } catch (err) {
      console.error(err);
    }
    setConfirmOp(null);
  };

  // Фактические суммы по категориям за период (только счета общего баланса).
  // Для «завершённых» категорий учитывается факт, неистраченный остаток освобождается.
  const actualByCat = (type) => {
    const map = {};
    (transactions || []).forEach(t => {
      if (t.type !== type) return;
      if (!t.date || (startDate && t.date < startDate) || (endDate && t.date > endDate)) return;
      if (!isIncludedAccount(allAccounts, t.accountId)) return;
      const cat = categoryNameById(t.categoryId);
      if (cat) map[cat] = (map[cat] || 0) + (Number(t.amount) || 0);
    });
    return map;
  };
  const actualIncomeMap = actualByCat('income');
  const actualExpenseMap = actualByCat('expense');

  // Ежедневный остаток = (доходы - расходы + переводы + долги) / количество дней в периоде.
  // totalPlanned учитывает бюджет категории или сумму её операций (что больше):
  // пока операции в пределах бюджета — остаток не меняется, превысили — учитывается всё.
  // Плановые операции, уже выполненные вручную (покрытые реальными операциями), не резервируются
  // — это связывает план с ручными операциями, чтобы остаток не падал дважды.
  const effPeriod = { startDate, endDate, plannedIncomes, plannedExpenses, plannedTransfers, plannedDebtReturns };
  const eff = effectivePlanned(effPeriod, allCategories, allAccounts, transactions, endDate || todayISO());
  const effIncome = eff.incomes
    .filter(g => !g.finished)
    .reduce((s, g) => s + (g.operations || []).filter(o => !o.effectiveDone).reduce((x, o) => x + (Number(o.amount) || 0), 0), 0);
  const effExpense = eff.expenses
    .filter(g => !g.finished)
    .reduce((s, g) => s + (g.operations || []).filter(o => !o.effectiveDone).reduce((x, o) => x + (Number(o.amount) || 0), 0), 0);
  const effTransfersNet = plannedTransfersNet(regularAccounts, eff.transfers.filter(x => !x.effectiveDone));
  const effDebtsNet = plannedDebtReturnsNet(regularAccounts, eff.debts.filter(d => !d.effectiveDone));
  const totalIncome = totalPlanned(plannedIncomes, cat => actualIncomeMap[cat]);
  const totalExpense = totalPlanned(plannedExpenses, cat => actualExpenseMap[cat]);
  const transfersNet = plannedTransfersNet(regularAccounts, plannedTransfers);
  const debtsNet = plannedDebtReturnsNet(regularAccounts, plannedDebtReturns);
  // Остатки на счетах (входящих в общий баланс) на начало периода
  const startBalance = startingBalance(allAccounts, transactions, startDate);
  const days = (startDate && endDate)
    ? Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1
    : 0;
  // Фиксированный базовый дневной лимит — единая модель с главной и списком планов:
  // (стартовый остаток + полный план) ÷ все дни периода. Не пересчитывается после покупок.
  const dailyModel = dailyBudgetModel(effPeriod, allCategories, allAccounts, transactions, endDate || todayISO());
  const daily = dailyModel ? dailyModel.baseDailyLimit : 0;

  // Группы по категориям: { category, amount (бюджет), operations: [{amount, date, comment}] }
  const cleanGroups = (groups) => (groups || []).map(g => ({
    category: g.category,
    amount: Number(g.amount) || 0,
    finished: !!g.finished,
    operations: (g.operations || []).map(o => ({
      id: o.id,
      amount: Number(o.amount) || 0,
      date: o.date || '',
      comment: o.comment || '',
      done: !!o.done,
      recurring: !!o.recurring,
      recurringId: o.recurringId || null,
    })),
  }));
  // Переводы/долги: суммы и id счетов в числа (в полях формы они строки, на сервере — числа)
  const toNum = (arr, idFields = []) => (arr || []).map(item => {
    const out = { ...item, amount: Number(item.amount) || 0 };
    idFields.forEach(f => { if (item[f] != null) out[f] = Number(item[f]); });
    return out;
  });

  // Автосохранение периода: создаём при первом изменении, дальше просто обновляем.
  const savePeriod = async () => {
    const hasData = plannedIncomes.length || plannedExpenses.length || plannedTransfers.length || plannedDebtReturns.length;
    if (!periodIdRef.current && !hasData) return; // пустой новый период не создаём
    const s = startDate || todayISO();
    const e = endDate || toISODate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const payload = {
      startDate: s,
      endDate: e,
      plannedIncomes: cleanGroups(plannedIncomes),
      plannedExpenses: cleanGroups(plannedExpenses),
      plannedTransfers: toNum(plannedTransfers, ['fromAccountId', 'toAccountId']),
      plannedDebtReturns: toNum(plannedDebtReturns, ['accountId', 'debtAccountId']),
    };
    try {
      if (periodIdRef.current) {
        await api.updatePeriod(periodIdRef.current, payload);
      } else {
        const created = await api.createPeriod(payload);
        periodIdRef.current = created.id;
        // подставляем даты по умолчанию, чтобы был виден ежедневный остаток
        if (!startDate || !endDate) {
          skipNextSaveRef.current = true;
          if (!startDate) setStartDate(s);
          if (!endDate) setEndDate(e);
        }
      }
      refreshCache();
      window.dispatchEvent(new Event('data:changed'));
    } catch (err) {
      console.error(err);
    }
  };

  // Сохраняем при каждом изменении плана или дат (с небольшой задержкой).
  useEffect(() => {
    if (isFirstRef.current) { isFirstRef.current = false; return; }
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    const t = setTimeout(savePeriod, 400);
    return () => clearTimeout(t);
  }, [plannedIncomes, plannedExpenses, plannedTransfers, plannedDebtReturns, startDate, endDate]);

  // Открыть модал добавления: дата по умолчанию — первый день периода
  const openAddModal = (kind) => {
    const defDate = startDate || '';
    if (kind === 'income') setIncomeOp(prev => ({ ...prev, date: defDate }));
    else if (kind === 'expense') setExpenseOp(prev => ({ ...prev, date: defDate }));
    else if (kind === 'transfer') setTransferDraft(prev => ({ ...prev, date: defDate }));
    else if (kind === 'debt') setDebtDraft(prev => ({ ...prev, date: defDate }));
    setAddModal(kind);
  };

  // Рендер секции доходов/расходов: категория — заголовок, операции — список ниже
  const plannedSection = (kind) => {
    const isIncome = kind === 'income';
    const groups = isIncome ? plannedIncomes : plannedExpenses;
    const title = isIncome ? 'Доходы' : 'Расходы';
    const tint = isIncome ? 'income' : 'expense';
    // Фактические доходы/расходы по категориям за период — для «осталось в категории»
    const actualByCat = isIncome ? actualIncomeMap : actualExpenseMap;
    return accordionShell(kind, title, tint, isIncome ? totalIncome : totalExpense, (
      <>
        {groups.map((g, gIndex) => {
          const budget = (g.operations || []).reduce((s, o) => s + (Number(o.amount) || 0), 0);
          // Автоматически зачёркиваем операции, покрытые фактическими расходами по категории,
          // и считаем, сколько факта «ушло» на каждую операцию (для показа план/факт через дробь).
          const autoDone = new Set();
          const usedByOp = {};
          {
            const sorted = g.operations.map((o, i) => ({ o, i })).sort((a, b) => (a.o.date || '').localeCompare(b.o.date || ''));
            let remaining = actualByCat[g.category] || 0;
            for (const { o, i } of sorted) {
              const amt = Number(o.amount) || 0;
              if (remaining >= amt) {
                autoDone.add(i);
                usedByOp[i] = amt;
                remaining -= amt;
              } else {
                usedByOp[i] = Math.min(remaining, amt);
                break;
              }
            }
          }
          const actual = actualByCat[g.category] || 0;
          const remaining = budget - actual;
          return (
            <div key={gIndex} className={`planned-cat${isIncome ? ' planned-cat-income' : ' planned-cat-expense'}`}>
              <div className="planned-cat-head">
                <span className="planned-cat-name">{categoryEmoji(g.category)} {g.category || 'Без категории'}</span>
                <div className="planned-cat-nums">
                  <div className="planned-cat-budget">
                    {isIncome ? 'Получить в этом месяце' : 'Бюджет'}: <strong>{formatMoney(budget)}</strong>
                  </div>
                  <div className="planned-cat-actual">
                    {isIncome ? 'Получено' : 'Потрачено'}: {formatMoney(actual)}
                  </div>
                  <div className={`planned-cat-remaining${remaining < 0 ? ' negative' : ''}`}>
                    {isIncome ? 'Осталось получить' : 'Осталось'}: {formatMoney(remaining)}
                  </div>
                </div>
                {!readOnly && <button type="button" className="planned-remove" onClick={() => removeGroup(kind, gIndex)} title="Удалить категорию">✖</button>}
              </div>
              {g.operations.length > 0 ? (
                <ul className="planned-ops">
                  {g.operations.map((o, oIndex) => {
                    const isDone = o.done || autoDone.has(oIndex);
                    return (
                    <li
                      key={oIndex}
                      className={`planned-op${isDone ? ' done' : ''}`}
                      onClick={() => { if (!readOnly) startEditOp(kind, gIndex, oIndex, o); }}
                      title="Редактировать операцию"
                    >
                      <span className="planned-op-body">
                        <span className="planned-op-title">{g.category || 'Без категории'}{o.recurring ? ' 🔁' : ''}</span>
                        {o.comment && <span className="planned-op-comment">{o.comment}</span>}
                        <span className="planned-op-meta">{o.date ? formatDate(o.date) : 'без даты'}</span>
                      </span>
                      <span className="planned-op-amount">
                        <span className="planned-op-amount-plan">{formatMoney(o.amount)}</span>
                        <span className={`planned-op-amount-used${usedByOp[oIndex] > 0 ? ' used' : ''}`}>
                          / {formatMoney(usedByOp[oIndex] || 0)}
                        </span>
                      </span>
                      {!readOnly && (
                        <span className="planned-op-actions">
                          <span
                            className="planned-op-check"
                            onClick={(e) => { e.stopPropagation(); openConfirmOp(kind, gIndex, oIndex, o, g.category); }}
                            title="Выполнить операцию"
                          >✓</span>
                          <span
                            className="planned-op-edit"
                            onClick={(e) => { e.stopPropagation(); startEditOp(kind, gIndex, oIndex, o); }}
                            title="Редактировать операцию"
                          >✏️</span>
                          <span
                            className="planned-op-del"
                            onClick={(e) => { e.stopPropagation(); removeOperation(kind, gIndex, oIndex); }}
                            title="Удалить операцию"
                          >✖</span>
                        </span>
                      )}
                    </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="planned-ops-empty">Операций нет</div>
              )}
              <div className="planned-group-foot">
                {!readOnly && (g.finished ? (
                  <span className="planned-finished-badge">✓ {isIncome ? 'Доходы' : 'Расходы'} по категории закончены</span>
                ) : (
                  <button type="button" className="secondary" onClick={() => finishCategory(kind, gIndex)}>
                    Закончить {isIncome ? 'доходы' : 'расходы'} в этом периоде
                  </button>
                ))}
                {!readOnly && g.finished && (
                  <button type="button" className="secondary" onClick={() => finishCategory(kind, gIndex)}>↩ Отменить</button>
                )}
                {g.finished && <span className="planned-finished-badge" style={{ marginLeft: 8 }}>
                  ✓ Учтено {formatMoney(actual)}{budget - actual > 0 ? ` из ${formatMoney(budget)}` : ''}
                </span>}
              </div>
            </div>
          );
        })}
        {groups.length === 0 && (
          <div className="planned-empty">{isIncome ? 'Доходов пока нет' : 'Расходов пока нет'}</div>
        )}
        {!readOnly && <button type="button" className="planned-add-btn" onClick={() => openAddModal(kind)}>
          {isIncome ? '➕ Добавить доход' : '➕ Добавить расход'}
        </button>}
      </>
    ));
  };

  // Каркас сворачиваемого блока-аккордеона
  const accordionShell = (type, title, tint, sum, children) => {
    const open = openSection === type;
    return (
      <div key={type} className={`plan-block plan-block-${tint}${open ? ' plan-block-open' : ''}`}>
        <button type="button" className={`plan-head${open ? ' open' : ''}`} onClick={() => toggleSection(type)}>
          <span className="plan-head-title">{title}</span>
          <span className="plan-head-sum">{formatMoney(sum || 0)}</span>
          <span className="plan-head-arrow">▾</span>
        </button>
        <div className={`collapsible-body${open ? ' open' : ''}`}>
          <div className={`plan-body${expandedDone[type] ? ' plan-body-open' : ''}`}>
            {children}
          </div>
        </div>
      </div>
    );
  };

  // Переводы — сворачиваемый блок (карточки, как у категорий)
  const transfersSum = plannedTransfers.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const transfersSection = () => accordionShell('transfer', 'Переводы', 'transfer', transfersSum, (
    <>
      {plannedTransfers.map((item, index) => {
        const isDone = !!(eff.transfers[index] && eff.transfers[index].effectiveDone);
        const fromName = accountNameById(item.fromAccountId);
        const toName = accountNameById(item.toAccountId);
        return (
          <div
            key={index}
            className={`planned-cat planned-cat-transfer${isDone ? ' done' : ''}`}
            onClick={() => { if (!readOnly) startEditTransfer(index); }}
            title="Редактировать перевод"
          >
            <div className="planned-cat-head">
              <span className="planned-cat-name">Перевод</span>
              <div className="planned-cat-nums">
                <div className="planned-cat-budget">Сумма: <strong>{formatMoney(item.amount)}</strong></div>
                <div className="planned-cat-actual">
                  {item.date ? formatDate(item.date) : 'без даты'}
                  {isDone && <span className="planned-op-amount-used used"> · выполнено</span>}
                </div>
              </div>
              {!readOnly && (
                <span className="planned-op-actions">
                  <span
                    className="planned-op-check"
                    onClick={(e) => { e.stopPropagation(); openConfirmTransfer(index); }}
                    title="Выполнить перевод"
                  >✓</span>
                  <span
                    className="planned-op-edit"
                    onClick={(e) => { e.stopPropagation(); startEditTransfer(index); }}
                    title="Редактировать"
                  >✏️</span>
                  <span
                    className="planned-op-del"
                    onClick={(e) => { e.stopPropagation(); removeTransfer(index); }}
                    title="Удалить"
                  >✖</span>
                </span>
              )}
            </div>
            <div className="planned-cat-lines">
              <div className="planned-cat-line">
                <span className="planned-cat-line-label">Со счёта</span>
                <span className="planned-cat-line-value">{fromName || '…'}</span>
              </div>
              <div className="planned-cat-line">
                <span className="planned-cat-line-label">На счёт</span>
                <span className="planned-cat-line-value">{toName || '…'}</span>
              </div>
            </div>
          </div>
        );
      })}
      {plannedTransfers.length === 0 && (
        <div className="planned-empty">Переводов пока нет</div>
      )}
      {!readOnly && <button type="button" className="planned-add-btn" onClick={() => { setEditingTransfer(null); openAddModal('transfer'); }}>➕ Добавить перевод</button>}
    </>
  ));

  // Долги — сворачиваемый блок (карточки, как у категорий)
  const debtsSum = plannedDebtReturns.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const debtsSection = () => accordionShell('debt', 'Долги', 'debt', debtsSum, (
    <>
      {plannedDebtReturns.map((item, index) => {
        const isDone = !!(eff.debts[index] && eff.debts[index].effectiveDone);
        const personName = debtAccountNameById(item.debtAccountId);
        const accName = accountNameById(item.accountId);
        return (
          <div
            key={index}
            className={`planned-cat planned-cat-debt${isDone ? ' done' : ''}`}
            onClick={() => { if (!readOnly) startEditDebtReturn(index); }}
            title="Редактировать возврат долга"
          >
            <div className="planned-cat-head">
              <span className="planned-cat-name">Возврат долга</span>
              <div className="planned-cat-nums">
                <div className="planned-cat-budget">Сумма: <strong>{formatMoney(item.amount)}</strong></div>
                <div className="planned-cat-actual">
                  {item.date ? formatDate(item.date) : 'без даты'}
                  {isDone && <span className="planned-op-amount-used used"> · выполнено</span>}
                </div>
              </div>
              {!readOnly && (
                <span className="planned-op-actions">
                  <span
                    className="planned-op-check"
                    onClick={(e) => { e.stopPropagation(); openConfirmDebtReturn(index); }}
                    title="Выполнить возврат долга"
                  >✓</span>
                  <span
                    className="planned-op-edit"
                    onClick={(e) => { e.stopPropagation(); startEditDebtReturn(index); }}
                    title="Редактировать"
                  >✏️</span>
                  <span
                    className="planned-op-del"
                    onClick={(e) => { e.stopPropagation(); removeDebtReturn(index); }}
                    title="Удалить"
                  >✖</span>
                </span>
              )}
            </div>
            <div className="planned-cat-lines">
              <div className="planned-cat-line">
                <span className="planned-cat-line-label">Кому / от кого</span>
                <span className="planned-cat-line-value">{personName || '…'}</span>
              </div>
              <div className="planned-cat-line">
                <span className="planned-cat-line-label">Счёт</span>
                <span className="planned-cat-line-value">{accName || '…'}</span>
              </div>
            </div>
          </div>
        );
      })}
      {plannedDebtReturns.length === 0 && (
        <div className="planned-empty">Долгов пока нет</div>
      )}
      {!readOnly && <button type="button" className="planned-add-btn" onClick={() => { setEditingDebt(null); openAddModal('debt'); }}>➕ Добавить возврат долга</button>}
    </>
  ));

  // ===== Модальные окна добавления =====
  const addOpModal = (kind) => {
    const isIncome = kind === 'income';
    const op = isIncome ? incomeOp : expenseOp;
    const setOp = isIncome ? setIncomeOp : setExpenseOp;
    const cats = isIncome ? incomeCategories : expenseCategories;
    const onCatChange = isIncome ? onIncomeCatChange : onExpenseCatChange;
    return (
      <Modal title={isIncome ? 'Добавить доход' : 'Добавить расход'} onClose={() => setAddModal(null)}>
        <div className="form-group">
          <label>Категория</label>
          <select value={op.category} onChange={(e) => onCatChange(e.target.value)}>
            <option value="">{isIncome ? 'Категория дохода' : 'Категория расхода'}</option>
            {cats.map(c => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
            <option value="__new__">➕ Новая категория</option>
          </select>
          {newCat && newCat.type === kind && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <input
                type="text"
                placeholder="Название новой категории"
                value={newCat.name}
                autoFocus
                onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(kind); } }}
              />
              <button type="button" onClick={() => handleCreateCategory(kind)}>OK</button>
            </div>
          )}
        </div>
        <div className="form-group">
          <label>Дата (не обяз.)</label>
          <DatePicker value={op.date} onChange={(d) => setOp(prev => ({ ...prev, date: d }))} placeholder="Дата платежа" />
        </div>
        <div className="form-group">
          <label>Сумма</label>
          <input type="number" inputMode="decimal" placeholder="Сумма" value={op.amount} onChange={(e) => setOp(prev => ({ ...prev, amount: e.target.value }))} />
        </div>
        <div className="form-group">
          <label>Комментарий (не обяз.)</label>
          <input type="text" placeholder="Комментарий" value={op.comment} onChange={(e) => setOp(prev => ({ ...prev, comment: e.target.value }))} />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
            <input type="checkbox" checked={!!op.recurring} onChange={(e) => setOp(prev => ({ ...prev, recurring: e.target.checked }))} />
            Повторять операцию каждый месяц
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={() => { if (addOperation(kind)) setAddModal(null); }}>Добавить</button>
          <button type="button" className="secondary" onClick={() => setAddModal(null)}>Отмена</button>
        </div>
      </Modal>
    );
  };

  const addTransferModal = () => {
    const isEdit = editingTransfer !== null;
    return (
    <Modal title={isEdit ? 'Редактировать перевод' : 'Добавить перевод'} onClose={() => setAddModal(null)}>
      <div className="form-group">
        <label>Со счёта</label>
        <select value={transferDraft.fromAccountId} onChange={(e) => setTransferDraft({ ...transferDraft, fromAccountId: e.target.value })}>
          <option value="">Со счёта</option>
          {regularAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>На счёт</label>
        <select value={transferDraft.toAccountId} onChange={(e) => setTransferDraft({ ...transferDraft, toAccountId: e.target.value })}>
          <option value="">На счёт</option>
          {regularAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Дата (не обяз.)</label>
        <DatePicker value={transferDraft.date} onChange={(d) => setTransferDraft({ ...transferDraft, date: d })} placeholder="Дата" />
      </div>
      <div className="form-group">
        <label>Сумма</label>
        <input type="number" inputMode="decimal" placeholder="Сумма" value={transferDraft.amount} onChange={(e) => setTransferDraft({ ...transferDraft, amount: e.target.value })} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={() => {
          const amt = Number(transferDraft.amount);
          if (!amt || amt <= 0) { alert('Укажите сумму'); return; }
          if (!transferDraft.fromAccountId || !transferDraft.toAccountId) { alert('Выберите оба счёта'); return; }
          if (transferDraft.fromAccountId === transferDraft.toAccountId) { alert('Счета должны отличаться'); return; }
          const newItem = { fromAccountId: transferDraft.fromAccountId, toAccountId: transferDraft.toAccountId, amount: amt, date: transferDraft.date || '', done: false };
          if (isEdit) {
            setPlannedTransfers(prev => prev.map((x, i) => i === editingTransfer ? { ...newItem, done: !!x.done } : x));
          } else {
            setPlannedTransfers([...plannedTransfers, newItem]);
          }
          setTransferDraft({ fromAccountId: '', toAccountId: '', amount: '', date: '' });
          setEditingTransfer(null);
          setAddModal(null);
        }}>{isEdit ? 'Сохранить' : 'Добавить'}</button>
        <button type="button" className="secondary" onClick={() => setAddModal(null)}>Отмена</button>
      </div>
    </Modal>
    );
  };

  const addDebtModal = () => {
    const isEdit = editingDebt !== null;
    return (
    <Modal title={isEdit ? 'Редактировать возврат долга' : 'Добавить возврат долга'} onClose={() => setAddModal(null)}>
      <div className="form-group">
        <label>Кому/от кого</label>
        <select value={debtDraft.debtAccountId} onChange={(e) => setDebtDraft({ ...debtDraft, debtAccountId: e.target.value })}>
          <option value="">Кому/от кого</option>
          {debtAccounts.map(a => <option key={a.id} value={a.id}>{a.person || a.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Счёт</label>
        <select value={debtDraft.accountId} onChange={(e) => setDebtDraft({ ...debtDraft, accountId: e.target.value })}>
          <option value="">Счёт</option>
          {regularAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>Дата (не обяз.)</label>
        <DatePicker value={debtDraft.date} onChange={(d) => setDebtDraft({ ...debtDraft, date: d })} placeholder="Дата" />
      </div>
      <div className="form-group">
        <label>Сумма</label>
        <input type="number" inputMode="decimal" placeholder="Сумма" value={debtDraft.amount} onChange={(e) => setDebtDraft({ ...debtDraft, amount: e.target.value })} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={() => {
          const amt = Number(debtDraft.amount);
          if (!amt || amt <= 0) { alert('Укажите сумму'); return; }
          if (!debtDraft.debtAccountId || !debtDraft.accountId) { alert('Выберите человека и счёт'); return; }
          const newItem = { debtAccountId: debtDraft.debtAccountId, accountId: debtDraft.accountId, amount: amt, date: debtDraft.date || '', done: false };
          if (isEdit) {
            setPlannedDebtReturns(prev => prev.map((x, i) => i === editingDebt ? { ...newItem, done: !!x.done } : x));
          } else {
            setPlannedDebtReturns([...plannedDebtReturns, newItem]);
          }
          setDebtDraft({ debtAccountId: '', accountId: '', amount: '', date: '' });
          setEditingDebt(null);
          setAddModal(null);
        }}>{isEdit ? 'Сохранить' : 'Добавить'}</button>
        <button type="button" className="secondary" onClick={() => setAddModal(null)}>Отмена</button>
      </div>
    </Modal>
    );
  };

  return (
    <>
    <form className="card period-form-card" onSubmit={(e) => e.preventDefault()}>
      <h3>{period ? (readOnly ? 'Период (просмотр)' : 'Редактировать период') : 'Новый период'}</h3>
      {readOnly && <div className="planned-finished-badge" style={{ display: 'block', marginBottom: 10 }}>🔒 Прошедший период — только просмотр</div>}
      <div className="form-group">
        <label>Дата начала</label>
        <DatePicker value={startDate} onChange={setStartDate} placeholder="Выберите дату начала" />
      </div>
      <div className="form-group">
        <label>Дата окончания</label>
        <DatePicker value={endDate} onChange={setEndDate} placeholder="Выберите дату окончания" />
      </div>

      {plannedSection('income')}
      {plannedSection('expense')}
      {transfersSection()}
      {debtsSection()}

      {days > 0 && (
        <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '12px' }}>
          <div className="list-item-title">Ваш ежедневный остаток</div>
          <div className="list-item-sub">
            Остатки на счетах: {formatMoney(startBalance)} · Доходы: {formatMoney(totalIncome)} · Расходы: {formatMoney(totalExpense)} · Дней: {days}
          </div>
          {(transfersNet !== 0 || debtsNet !== 0) && (
            <div className="list-item-sub">
              Переводы: {formatMoney(transfersNet)} · Долги: {formatMoney(debtsNet)}
            </div>
          )}
          <div className={`big-number${daily < 0 ? ' negative' : ''}`} style={{ fontSize: '24px', fontWeight: 800 }}>
            {formatMoney(daily)} <span style={{ fontSize: '14px', fontWeight: 400 }}>/ день</span>
          </div>
          {daily < 0 && <small className="muted">Расходы превышают доходы — проверьте планирование.</small>}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        {readOnly ? (
          <button type="button" onClick={onCancel}>Закрыть</button>
        ) : (
          <button type="button" onClick={() => { savePeriod(); onCancel(); }}>Готово</button>
        )}
      </div>
    </form>

    {!readOnly && addModal === 'income' && addOpModal('income')}
    {!readOnly && addModal === 'expense' && addOpModal('expense')}
    {!readOnly && addModal === 'transfer' && addTransferModal()}
    {!readOnly && addModal === 'debt' && addDebtModal()}

    {!readOnly && editingOp && (
      <Modal title="Редактировать операцию" onClose={() => setEditingOp(null)}>
        <div className="form-group">
          <label>Сумма</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={editingOp.amount}
            autoFocus
            onChange={(e) => setEditingOp({ ...editingOp, amount: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEditOp(); } }}
          />
        </div>
        <div className="form-group">
          <label>Дата (не обяз.)</label>
          <DatePicker value={editingOp.date} onChange={(d) => setEditingOp({ ...editingOp, date: d })} placeholder="Дата платежа" />
        </div>
        <div className="form-group">
          <label>Комментарий (не обяз.)</label>
          <input
            type="text"
            value={editingOp.comment}
            onChange={(e) => setEditingOp({ ...editingOp, comment: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
            <input type="checkbox" checked={!!editingOp.recurring} onChange={(e) => setEditingOp({ ...editingOp, recurring: e.target.checked })} />
            Повторять операцию каждый месяц
          </label>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={{ background: 'var(--danger)', color: '#fff' }}
            onClick={() => { removeOperation(editingOp.kind, editingOp.gIndex, editingOp.oIndex); setEditingOp(null); }}
          >Удалить</button>
          <button type="button" onClick={saveEditOp}>Сохранить</button>
          <button type="button" className="secondary" onClick={() => setEditingOp(null)}>Отмена</button>
        </div>
      </Modal>
    )}

    {!readOnly && confirmOp && (
      <Modal title="Выполнить операцию" onClose={() => setConfirmOp(null)}>
        <p style={{ color: 'var(--text-dim)', marginTop: 0 }}>
          Запланировано: {confirmOp.kind === 'transfer'
            ? `Перевод: ${accountNameById(confirmOp.fromAccountId)} → ${accountNameById(confirmOp.toAccountId)}`
            : confirmOp.kind === 'debtReturn'
              ? `Возврат долга: ${debtAccountNameById(confirmOp.debtAccountId)}`
              : confirmOp.category}
          {' '}· {formatMoney(confirmOp.amount)}
        </p>
        <TransactionForm
          categories={allCategories}
          periodId={periodIdRef.current}
          transaction={confirmTx}
          onSave={handleConfirmOp}
          onCancel={() => setConfirmOp(null)}
        />
      </Modal>
    )}
    </>
  );
}
